import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { useResubmitVendorApplicationMutation } from "generated/graphql";
import { useSnack } from "hooks/useSnack";

interface ApplicationNoticeProps {
  rejectionReason: string | null;
}

/** Why the application was rejected, and the button that sends it back. */
const ApplicationNotice: React.FC<ApplicationNoticeProps> = ({
  rejectionReason,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("profile");
  const [, resubmit] = useResubmitVendorApplicationMutation();
  const [submitting, setSubmitting] = useState(false);

  const handleResubmit = async () => {
    setSubmitting(true);
    const response = await resubmit({});
    setSubmitting(false);

    const error =
      response.data?.resubmitVendorApplication.errors?.[0]?.message ??
      response.error?.message;
    if (error) {
      showSnack(error, "error");
      return;
    }
    showSnack(t("application.resubmitted"), "success");
    navigate("/dashboard/profile");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 2,
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" style={{ fontWeight: "bold" }}>
          {t("application.title")}
        </Typography>
        {rejectionReason && (
          <Typography variant="body2">{rejectionReason}</Typography>
        )}
        <Typography variant="body2" style={{ opacity: 0.7 }}>
          {t("application.body")}
        </Typography>
      </Box>
      <Button
        variant="contained"
        color="warning"
        style={{ color: theme.palette.common.white, fontWeight: "bold" }}
        onClick={handleResubmit}
        loading={submitting}
        disabled={submitting}
      >
        {t("application.resubmit")}
      </Button>
    </Box>
  );
};

export default ApplicationNotice;
